import React, { useState } from 'react';

/**
 * Priority-slots section rendered at the top of To Do Today (main list only).
 *
 * Slots are resolved read-only by the backend and arrive on the main list's
 * `priority_slots`. Fulfilled slots are already omitted server-side. Each slot is a
 * task-shaped outlined box: a filled slot frames its picked task (via the `renderTask`
 * callback so all the normal TodoTask wiring lives in the parent); an empty slot is a
 * dashed placeholder naming the list whose tasks are allowed to fill it.
 *
 * When a slotted task is completed we must persist the slot as fulfilled *before*
 * (or alongside) the completion reload, otherwise the next resolve would re-fill the
 * slot with a different task instead of hiding it. We optimistically hide the slot,
 * await the fulfill write, and re-show + surface an error if it fails.
 */
export default function PrioritySlotsSection({
  slots,
  listId,
  onFulfillSlot,
  onToggleTask,
  renderTask,
}) {
  const [hiddenSlotIds, setHiddenSlotIds] = useState(() => new Set());
  const [error, setError] = useState(null);

  const hide = (slotId) => setHiddenSlotIds(prev => {
    const next = new Set(prev);
    next.add(slotId);
    return next;
  });
  const unhide = (slotId) => setHiddenSlotIds(prev => {
    const next = new Set(prev);
    next.delete(slotId);
    return next;
  });

  const handleComplete = async (slot) => {
    const slotId = slot.slot_id;
    setError(null);
    // Optimistically hide the slot for snappy UX.
    hide(slotId);
    try {
      // Persist fulfillment first so the completion reload sees fulfilled_date=today
      // and omits the slot (rather than re-filling it with another candidate).
      await onFulfillSlot(slotId);
    } catch (err) {
      // Dropped write: re-show the slot and warn. Completion still proceeds below.
      unhide(slotId);
      setError('Could not mark the priority slot complete — it may reappear.');
    }
    try {
      await onToggleTask(listId, slot.task.id);
    } finally {
      // Authoritative data has reloaded; let server state drive visibility again.
      unhide(slotId);
    }
  };

  const visibleSlots = slots.filter(s => !hiddenSlotIds.has(s.slot_id));
  if (visibleSlots.length === 0) return null;

  return (
    <div className="priority-slots-section">
      {error && (
        <div className="notification notification-error" style={{ marginBottom: '0.5rem' }}>
          {error}
        </div>
      )}

      <div className="priority-slots-list">
        {visibleSlots.map((slot) => (
          slot.task ? (
            <div key={slot.slot_id} className="priority-slot priority-slot--filled">
              {renderTask(slot.task, () => handleComplete(slot))}
            </div>
          ) : (
            <div key={slot.slot_id} className="priority-slot priority-slot--empty">
              <span className="priority-slot-ghost-check" aria-hidden="true" />
              <span className="priority-slot-hint">Promote a task to fill this slot</span>
              <span className="priority-slot-list-badge" title={slot.list_name}>
                {slot.list_name}
              </span>
            </div>
          )
        ))}
      </div>
    </div>
  );
}
